import React from 'react';
import { useTranslation } from 'react-i18next';
import PriceTag from '../ui/PriceTag';
import { formatDateTime } from '../../utils/formatDate';

const STATUS_OPTIONS = ['pending', 'preparing', 'ready', 'completed'];

export default function OrderCard({
  order,
  currency,
  locale,
  isActive,
  updatingId,
  onStatusChange,
  onViewDetails,
}) {
  const { t } = useTranslation();
  const statusLabel = (value) => t(`orders.status.${value}`);
  const resolvedCurrency = currency || 'USD';

  return (
    <div className="order-card">
      <div className="order-card-header">
        <div className="order-card-meta">
          <strong>
            {order.order_type === 'car' ? (() => {
              const colorLabel = order.car_color
                ? t(`menu.cart.carColors.${order.car_color}`, { defaultValue: order.car_color })
                : '';
              return `🚗 ${[colorLabel, order.car_number].filter(Boolean).join(' ')}`;
            })() : `${t('orders.table')} ${order.table_number}`}
          </strong>
          <span className="order-card-time">
            {formatDateTime(order.created_at, locale)}
          </span>
        </div>
        <div className="orders-card-header-right">
          <div className="order-card-total">
            <PriceTag amount={order.total_amount} currency={resolvedCurrency} locale={locale} size="md" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
          </div>
          <button
            type="button"
            className="btn btn-secondary order-view-details-btn"
            onClick={() => onViewDetails(order)}
          >
            {t('orders.viewDetails')}
          </button>
        </div>
      </div>
      {order.special_instructions && (
        <div className="order-special-instructions">
          <span className="instructions-icon">📝</span>
          <span className="instructions-label">{t('orders.specialInstructions')}:</span>
          <span className="instructions-text">{order.special_instructions}</span>
        </div>
      )}
      {isActive ? (
        <div className="order-card-status-row">
          <span className="order-status-label">{t('orders.status.label')}</span>
          <div className="order-status-buttons">
            {STATUS_OPTIONS.map((statusValue) => (
              <button
                key={statusValue}
                type="button"
                className={`order-status-btn ${order.status === statusValue ? 'order-status-btn--active' : ''} order-status-btn--${statusValue}`}
                onClick={() => onStatusChange(order.id, statusValue)}
                disabled={updatingId === order.id || order.status === statusValue}
                title={order.status === statusValue ? t('orders.status.currentStatus') : t('orders.status.setTo', { status: statusLabel(statusValue) })}
              >
                {statusLabel(statusValue)}
              </button>
            ))}
          </div>
          {updatingId === order.id && <span className="order-updating">{t('orders.status.updating')}</span>}
        </div>
      ) : (
        <div className="order-status-badge-row">
          <span className={`order-status-badge order-status-badge--${order.status}`}>
            {t(`orders.status.${order.status}`)}
          </span>
          {(order.completed_at || order.served_at) && (
            <span className="order-completed-time">
              {t('orders.completedAt')}: {formatDateTime(order.completed_at || order.served_at, locale)}
            </span>
          )}
        </div>
      )}
      {order.items && order.items.length > 0 && (
        <ul className="order-card-items">
          {order.items.map((item, idx) => (
            <li key={item.id || idx}>
              <div className="order-item-main">
                <span className="order-item-qty">{item.quantity}×</span>
                <span className="order-item-name">{item.name}</span>
                <span className="order-item-price">
                  <PriceTag
                    amount={(parseFloat(item.price) || 0) * (item.quantity || 1)}
                    currency={resolvedCurrency}
                    locale={locale}
                    size="sm"
                    color="inherit"
                    style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                  />
                </span>
              </div>
              {item.selected_addons && item.selected_addons.length > 0 && (
                <div className="item-addons">
                  {item.selected_addons.map((addon, addonIdx) => (
                    <div key={addonIdx} className="addon-item">
                      <span className="addon-icon">+</span>
                      <span className="addon-name">{addon.name}</span>
                      {addon.price != null && parseFloat(addon.price) > 0 && (
                        <span className="addon-price" style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.15em' }}>
                          +<PriceTag amount={addon.price} currency={resolvedCurrency} locale={locale} size="sm" color="inherit" style={{ fontSize: 'inherit', fontWeight: 'inherit' }} />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
